/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { getSharedState } from "@microsoft/shifts-experiences-shared";
import { ThemeType } from "@microsoft/shifts-ui-core";
import {
    FeedbackAuthenticationType,
    FeedbackHostPlatformType,
    FeedbackInitData,
    FeedbackPolicyValue,
    FeedbackScreenshotSourceType,
    FeedbackType,
    IFeedbackConfig,
    IFeedbackInitOptions,
    IThemeOptions,
    initializeInAppFeedback,
    openFeedback
} from "@ms/inapp-feedback-loader";

import { getGenericEventPropertiesObject } from "../../../framework/sh-instrumentation";
import { MicrosoftTeamsClient, MicrosoftTeams_WEB_OCV_APPID, MicrosotTeams_WINDOWS_OCV_APPID } from "../../../staffhub/StaffHubConstants";
import { InstrumentationService } from "../../../staffhub/sh-services";

import { FeedbackScenario } from "./FeedbackScenario";
import { LoadFeedbackParams } from "./LoadFeedbackParams";
import { OpenFeedbackPanelParams } from "./OpenFeedbackPanelParams";

// TODO feedback: Improve telemetry for each callback.

// function onDismissPrint(isFeedbackSent?: boolean) {
//     logEvent(InstrumentationService.values.FeedbackEntryDismissed, isFeedbackSent);
// }

/**
 * Sends feedback onError callback.
 * @param errorMessage ErrorMessage of the error.
 */
function onErrorLog(errorMessage?: string): void {
    logEvent(InstrumentationService.values.FeedbackEntryErrored, true);
    InstrumentationService.trackException(new Error(errorMessage), "FeedbackTeams.callbacks.onError");
}
// function onSuccessPrint(clientFeedbackId?: string) {
//     logEvent(InstrumentationService.values.FeedbackEntrySucceeded, true);
// }
// function initializationCompletePrint(initializationCompleteResult: InitializationResult) {
//     logEvent(InstrumentationService.values.FeedbackInitializationComplete, true);
// }

/**
 * Loads the feedback component and bootstraps it.
 * @param params The data required to load and bootstrap the feedback component.
 */
export async function loadFeedbackAndBootstrap(params: LoadFeedbackParams): Promise<void> {
    const { inAppFeedbackElement } = params;

    try {
        let feedbackInfo: FeedbackInitData;
        const { locale } = getSharedState();
        if (inAppFeedbackElement && locale) {
            feedbackInfo = {
                // NOTE: providing a hostname not registered with Centro will result in an silent error and the panel will not be displayed.
                hostName: MicrosoftTeamsClient,
                inAppFeedbackRef: inAppFeedbackElement,
                locale
            };
            // TODO feedback: Add loading and error states for feedback initialization
            // TODO feedback: Add telemetry for feedback initialization
            await initializeInAppFeedback(feedbackInfo);
        } else {
            throw new Error("one of the following parameter is missing or incorrect: inAppFeedbackRef, hostName, locale");
        }
    } catch (e) {
        InstrumentationService.trackException(e, "FeedbackTeams.loadFeedbackAndBootstrap");
    }
}

/**
 * Opens the feedback dialog for the specified scenario.
 * @param params The data required to open the feedback dialog.
 */
export function openFeedbackPanel(
    params: OpenFeedbackPanelParams
): void {
    const { tenantId, userId, userEmail, scenario } = params;
    const { theme } = getSharedState();

    // TODO feedback: Replace with actual theme, may need to build a theme mapping for this
    let themeOptions: IThemeOptions;
    switch (theme) {
        case ThemeType.Dark:
            themeOptions = { baseTheme: "TeamsDarkV2" };
            break;
        case ThemeType.Light:
            themeOptions = { baseTheme: "TeamsLightV2" };
            break;
        case ThemeType.HighContrast:
            themeOptions = { baseTheme: "TeamsHighContrastV2" };
            break;
        default:
            themeOptions = { baseTheme: "TeamsLightV2" };
            break;
    }

    const feedbackConfig: IFeedbackConfig = {
        email: userEmail,
        initialFeedbackType: scenario == FeedbackScenario.GiveACompliment ? FeedbackType.Smile : FeedbackType.Frown,
        hostPlatform: params.feedbackHostPlatformType
    };

    const customFeedbackInitOptions: IFeedbackInitOptions = {
        feedbackConfig,
        tenantId,
        themeOptions,
        userId
    };

    const options: IFeedbackInitOptions = getFeedbackInitOptions(customFeedbackInitOptions);

    // Open the feedback dialog
    openFeedback(options);
}

/**
 * Gets the feedback initialization options.
 * @param feedbackInitOptions The feedback initialization options.
 * @returns The feedback initialization options.
 */
const getFeedbackInitOptions = (
    feedbackInitOptions: IFeedbackInitOptions
): IFeedbackInitOptions => {
    const { tenantId, userId, feedbackConfig, themeOptions } = feedbackInitOptions;

    const { email, initialFeedbackType, hostPlatform } = feedbackConfig;
    let appId: number;
    if (hostPlatform == FeedbackHostPlatformType.Web) {
        appId = MicrosoftTeams_WEB_OCV_APPID;
    } else if (hostPlatform == FeedbackHostPlatformType.Windows) {
        appId = MicrosotTeams_WINDOWS_OCV_APPID;
    }

    return {
        appId: appId, // Microsoft Teams AppId based on Host Platform
        authenticationType: FeedbackAuthenticationType.Unauthenticated,
        // TODO feedback: add telemetry for each of these callbacks
        callbackFunctions: {
          onError: onErrorLog
        },
        clientName: MicrosoftTeamsClient,
        feedbackConfig: {
            // complianceChecks is required for AAD users. We would need to query OCPS API to get the compliance checks and pass on to the feedback module
            complianceChecks: {
                connectedExperiences: FeedbackPolicyValue.Enabled,
                policyAllowContact: FeedbackPolicyValue.Enabled,
                policyAllowContent: FeedbackPolicyValue.Enabled,
                policyAllowFeedback: FeedbackPolicyValue.Enabled,
                policyAllowScreenshot: FeedbackPolicyValue.Enabled,
                policyAllowSurvey: FeedbackPolicyValue.Enabled,
                policyEmailCollectionDefault: FeedbackPolicyValue.Enabled,
                policyScreenshotDefault: FeedbackPolicyValue.Enabled,
                policyContentSamplesDefault: FeedbackPolicyValue.Enabled
            },
            email,
            initialFeedbackType,
            isEmailCollectionEnabled: true, // Adds "may we contact you" checkbox and email in the feedback form.
            isScreenshotEnabled: true, // Adds "Take a screenshot" button to the feedback form.
            isFileUploadEnabled: initialFeedbackType  == FeedbackType.Frown ? true : false, // Adds "Attach a file" button to the feedback form, only for negative feedback.
            screenshotInfo: {
                providedScreenshotType: FeedbackScreenshotSourceType.AutoCaptureWithHtml2Canvas
            },
            hostPlatform
        },
        telemetry: {
            featureArea: "Shifts" // This links the feature under MicrosoftTeams to Shifts.
        },
        isProduction: true,
        themeOptions, // TODO feedback: Replace with actual theme, may need to build a theme mapping for this
        tenantId,
        userId
    };
};

const logEvent = (eventType: string, isSuccess?: boolean): void => {
    const eventProps = [
        getGenericEventPropertiesObject(InstrumentationService.properties.EventType, eventType)
    ];

    if (typeof isSuccess == "boolean") {
        eventProps.push(getGenericEventPropertiesObject(InstrumentationService.properties.IsSuccess, isSuccess));
    }

    InstrumentationService.logEvent(InstrumentationService.events.OpenShiftsExperience, eventProps);
};